import {
  MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS,
  MANUSCRIPT_PAGE_MAXIMUM_WORDS,
} from "./childrensBookManuscriptUnitDivision";

import {
  countManuscriptWords,
  manuscriptFlowSlotWordLimit,
} from "./childrensBookManuscriptCapacity";

import {
  manuscriptTextBreakIsSentenceBoundary,
} from "./childrensBookManuscriptFlowProjection";

import {
  plainTextFromManuscriptContent,
} from "./childrensBookManuscriptContent";

import {
  manuscriptEntryFieldsFromEditorContent,
} from "./childrensBookManuscriptEditor";

import {
  prepareChildrensBookManuscriptLiveReflow,
} from "./childrensBookManuscriptLiveReflow";

import {
  mapChildrensBookManuscriptSelectionThroughReflow,
} from "./childrensBookManuscriptSelectionHandoff";


function clean(value) {
  return String(
    value ?? ""
  ).trim();
}


function safeArray(value) {
  return Array.isArray(value)
    ? value
    : [];
}


function normalizedSelection(
  value
) {
  const blockId =
    clean(
      value?.blockId
    );

  if (!blockId) {
    return null;
  }

  const start =
    Math.max(
      0,
      Number(
        value?.start
      ) || 0
    );

  const end =
    Math.max(
      0,
      Number(
        value?.end
      ) || 0
    );

  return {
    blockId,
    start,
    end,
  };
}


function entryFor(
  entries,
  unitId
) {
  return safeArray(
    entries
  ).find(
    (entry) =>
      clean(
        entry?.unitId
      ) ===
      clean(
        unitId
      )
  ) || null;
}


function entriesWithNormalizedEdit({
  entries = [],
  unitId = "",
  normalizedFields,
} = {}) {
  const sourceEntries =
    safeArray(
      entries
    );

  const exists =
    sourceEntries.some(
      (entry) =>
        clean(
          entry?.unitId
        ) ===
        clean(
          unitId
        )
    );

  const changedFields = {
    manuscriptText:
      normalizedFields
        .manuscriptText,

    manuscriptContent:
      normalizedFields
        .manuscriptContent,
  };

  if (exists) {
    return sourceEntries.map(
      (entry) =>
        clean(
          entry?.unitId
        ) ===
        clean(
          unitId
        )
          ? {
              ...entry,
              ...changedFields,
            }
          : entry
    );
  }

  return [
    ...sourceEntries,

    {
      unitId,
      manuscriptText: "",
      authorNote: "",
      status:
        "not_started",
      ...changedFields,
    },
  ];
}


/*
 * Natural writer Enter qualification.
 *
 * A paragraph split becomes a temporary physical-page boundary
 * only when:
 *   - it is an editor paragraph_break intent,
 *   - the split is a genuine sentence boundary in context, and
 *   - the current physical page is sufficiently occupied.
 *
 * The returned boundary is transient calculation authority only.
 */
export function childrensBookSharedParagraphBreakPageFlowRequest({
  unit,
  manuscriptContent,
  editIntent,
} = {}) {
  if (
    clean(
      editIntent?.type
    ) !==
      "paragraph_break"
  ) {
    return null;
  }

  const sourceBlockId =
    clean(
      editIntent
        ?.sourceBlockId
    );

  const continuationBlockId =
    clean(
      editIntent
        ?.continuationBlockId
    );

  const sourceOffset =
    Number(
      editIntent
        ?.sourceOffset
    );

  if (
    !sourceBlockId ||
    !continuationBlockId ||
    !Number.isFinite(
      sourceOffset
    ) ||
    sourceOffset < 0
  ) {
    return null;
  }

  const blocks =
    Array.isArray(
      manuscriptContent
        ?.blocks
    )
      ? manuscriptContent.blocks
      : [];

  const continuationIndex =
    blocks.findIndex(
      (block) =>
        clean(
          block?.id
        ) ===
        continuationBlockId
    );

  if (
    continuationIndex < 1
  ) {
    return null;
  }

  const sourceBlock =
    blocks[
      continuationIndex - 1
    ];

  const continuationBlock =
    blocks[
      continuationIndex
    ];

  if (
    clean(
      sourceBlock?.id
    ) !==
      sourceBlockId ||
    sourceBlock?.type ===
      "page_break" ||
    continuationBlock?.type ===
      "page_break"
  ) {
    return null;
  }

  const beforeContent = {
    ...manuscriptContent,

    blocks:
      blocks.slice(
        0,
        continuationIndex
      ),
  };

  const afterContent = {
    ...manuscriptContent,

    blocks:
      blocks.slice(
        continuationIndex
      ),
  };

  const beforeText =
    plainTextFromManuscriptContent(
      beforeContent
    );

  const afterText =
    plainTextFromManuscriptContent(
      afterContent
    );

  if (
    !manuscriptTextBreakIsSentenceBoundary(
      beforeText,
      afterText
    )
  ) {
    return null;
  }

  const pageWordLimit =
    manuscriptFlowSlotWordLimit(
      unit,
      MANUSCRIPT_PAGE_MAXIMUM_WORDS
    );

  if (
    !Number.isInteger(
      pageWordLimit
    ) ||
    pageWordLimit < 1
  ) {
    return null;
  }

  const preferredBoundaryWords =
    Math.max(
      1,

      Math.ceil(
        pageWordLimit *
        MANUSCRIPT_PAGE_PREFERRED_MINIMUM_WORDS /
        MANUSCRIPT_PAGE_MAXIMUM_WORDS
      )
    );

  const wordsBeforeBoundary =
    countManuscriptWords(
      beforeText
    );

  if (
    wordsBeforeBoundary <
      preferredBoundaryWords ||
    wordsBeforeBoundary >
      pageWordLimit
  ) {
    return null;
  }

  return {
    sourceBlockId,
    continuationBlockId,
  };
}


/*
 * Pure shared manuscript edit coordinator.
 *
 * This is intended to be used by every writer-facing surface that
 * edits the same manuscriptPlan.entries authority.
 *
 * It does not:
 *   - persist anything,
 *   - invoke React callbacks,
 *   - choose a workspace,
 *   - maintain component refs,
 *   - create Book Structure units, or
 *   - store editIntent / selectionHandoff.
 *
 * The caller remains responsible for committing nextEntries and
 * restoring/focusing any returned cursor position.
 */
export function prepareChildrensBookSharedManuscriptEdit({
  units = [],
  entries = [],
  unitId = "",
  manuscriptContent,
  selectionHandoff = null,
  editIntent = null,
  wordLimit,
} = {}) {
  const activeUnitId =
    clean(
      unitId
    );

  if (!activeUnitId) {
    return {
      ok: false,
      flowApplied: false,
      code:
        "active_unit_required",
      nextEntries:
        safeArray(
          entries
        ),
      targetUnitId: "",
      restorableSelection:
        null,
    };
  }

  const normalizedFields =
    manuscriptEntryFieldsFromEditorContent(
      manuscriptContent
    );

  if (!normalizedFields.ok) {
    return {
      ok: false,
      flowApplied: false,
      code:
        "manuscript_content_invalid",
      errors:
        normalizedFields
          .errors || [],
      nextEntries:
        safeArray(
          entries
        ),
      targetUnitId:
        activeUnitId,
      restorableSelection:
        null,
    };
  }

  const currentEntry =
    entryFor(
      entries,
      activeUnitId
    );

  const currentStructuredFields =
    currentEntry
      ?.manuscriptContent
      ? manuscriptEntryFieldsFromEditorContent(
          currentEntry
            .manuscriptContent
        )
      : null;

  const currentText =
    currentStructuredFields
      ?.ok
      ? currentStructuredFields
          .manuscriptText
      : String(
          currentEntry
            ?.manuscriptText ||
          ""
        );

  const textChanged =
    normalizedFields
      .manuscriptText !==
    currentText;

  const sourceSelection =
    normalizedSelection(
      selectionHandoff
    );

  const collapsedCaret =
    Boolean(
      sourceSelection &&
      Number(
        sourceSelection.start
      ) ===
        Number(
          sourceSelection.end
        )
    );

  const directlyEditedEntries =
    entriesWithNormalizedEdit({
      entries,
      unitId:
        activeUnitId,
      normalizedFields,
    });

  if (
    !textChanged ||
    !collapsedCaret
  ) {
    return {
      ok: true,
      flowApplied: false,
      reason:
        !textChanged
          ? "text_unchanged"
          : "collapsed_caret_required",
      nextEntries:
        directlyEditedEntries,
      targetUnitId:
        activeUnitId,
      restorableSelection:
        null,
      application:
        null,
    };
  }

  const activeUnit =
    safeArray(
      units
    ).find(
      (unit) =>
        clean(
          unit?.id
        ) ===
        activeUnitId
    ) || null;

  const pageFlowBoundaryRequest =
    childrensBookSharedParagraphBreakPageFlowRequest({
      unit:
        activeUnit,

      manuscriptContent:
        normalizedFields
          .manuscriptContent,

      editIntent,
    });

  const liveReflow =
    prepareChildrensBookManuscriptLiveReflow({
      units,
      entries,

      activeUnitId,

      changedFields: {
        manuscriptText:
          normalizedFields
            .manuscriptText,

        manuscriptContent:
          normalizedFields
            .manuscriptContent,
      },

      wordLimit,
      pageFlowBoundaryRequest,
    });

  if (!liveReflow?.ok) {
    return {
      ok: true,
      flowApplied: false,
      reason:
        liveReflow?.code ||
        "live_reflow_unavailable",
      nextEntries:
        directlyEditedEntries,
      targetUnitId:
        activeUnitId,
      restorableSelection:
        null,
      application:
        liveReflow
          ?.application ||
        null,
    };
  }

  if (
    !liveReflow
      .flowApplied
  ) {
    return {
      ok: true,
      flowApplied: false,
      reason:
        liveReflow.reason ||
        "automatic_flow_not_applied",
      nextEntries:
        liveReflow
          .nextEntries,
      targetUnitId:
        activeUnitId,
      restorableSelection:
        null,
      application:
        liveReflow
          .application ||
        null,
    };
  }

  const flowSlotUnitIds =
    Array.isArray(
      liveReflow
        ?.application
        ?.consultation
        ?.flowSlotUnitIds
    )
      ? liveReflow
          .application
          .consultation
          .flowSlotUnitIds
          .map(
            clean
          )
          .filter(
            Boolean
          )
      : [];

  if (
    !flowSlotUnitIds.length
  ) {
    return {
      ok: true,
      flowApplied: false,
      reason:
        "selection_flow_slots_unavailable",
      nextEntries:
        directlyEditedEntries,
      targetUnitId:
        activeUnitId,
      restorableSelection:
        null,
      application:
        liveReflow
          .application ||
        null,
    };
  }

  const mappedSelection =
    mapChildrensBookManuscriptSelectionThroughReflow({
      orderedUnitIds:
        flowSlotUnitIds,

      targetEntries:
        liveReflow
          .nextEntries,

      selection:
        sourceSelection,
    });

  if (
    !mappedSelection?.ok ||
    mappedSelection
      .collapsed !== true ||
    !mappedSelection
      .restorableSelection
  ) {
    return {
      ok: true,
      flowApplied: false,
      reason:
        mappedSelection?.code ||
        "selection_reflow_unrestorable",
      nextEntries:
        directlyEditedEntries,
      targetUnitId:
        activeUnitId,
      restorableSelection:
        null,
      application:
        liveReflow
          .application ||
        null,
    };
  }

  const targetUnitId =
    clean(
      mappedSelection
        .restorableSelection
        .unitId
    );

  const targetEntry =
    liveReflow
      .nextEntries
      .find(
        (entry) =>
          clean(
            entry?.unitId
          ) ===
          targetUnitId
      );

  if (
    !targetUnitId ||
    !targetEntry
      ?.manuscriptContent
  ) {
    return {
      ok: true,
      flowApplied: false,
      reason:
        "selection_target_unavailable",
      nextEntries:
        directlyEditedEntries,
      targetUnitId:
        activeUnitId,
      restorableSelection:
        null,
      application:
        liveReflow
          .application ||
        null,
    };
  }

  return {
    ok: true,
    flowApplied: true,
    reason:
      liveReflow.reason ||
      "existing_page_flow_applied",

    nextEntries:
      liveReflow
        .nextEntries,

    targetUnitId,

    restorableSelection: {
      unitId:
        targetUnitId,

      selection:
        mappedSelection
          .restorableSelection
          .selection,
    },

    targetEntrySignature:
      JSON.stringify(
        targetEntry
          .manuscriptContent
      ),

    application:
      liveReflow
        .application,
  };
}
